import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CheckCircle2, Loader2 } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  createVersionFromMarketMatch,
  loadLampSimilarityDetails,
  startLampSimilarity,
  workspaceReducer,
} from '../../../../store/reducers/workspace.reducer.ts';
import { getUserFeatureFlags } from '../../../../store/selectors/featureFlags.selector';
import { getNorms } from '../../../../store/selectors/user.selector';
import { selectActiveVersion } from '../../../../store/selectors/workspace.selector.ts';
import { RankedCandidate } from '../../../../types/project.ts';
import { buildCatalogueUrl } from '../../../../utils/catalogueUrl';
import { MARKET_MATCH_TO_VERSION_FLAG } from '../../../../utils/featureFlagKeys';
import { isJobFailed, isJobInProgress } from '../../../../utils/jobStatus.ts';
import {
  canApplyMarketMatchCandidate,
  getMarketMatchPreviewMode,
  selectWorkspaceMeasurementData,
} from '../../../../utils/measurementCalculation/measurementSource';
import {
  getTargetsSatisfiedCount,
  getTargetsSatisfiedCountFromAggregateResults,
  measurementDataToAggregateResults,
} from '../../../../utils/normSatisfactionUtils.ts';
import MarketMatchIcon from '../../icons/MarketMatchIcon';
import { MarketMatchPrefiltersPanel } from '../../sidebar/MarketMatchPrefiltersPanel';
import { MarketMatchResultsPreviewModal } from '../../sidebar/MarketMatchResultsPreviewModal';
import { PanelSection } from '../PanelSection';
import { MARKET_PREFILTERS_FORM_ID } from '../types';

/** Market search: prefilters (submitted from the footer), progress, then ranked catalogue matches. */
export function MarketMatchSection({ onCanStartChange }: { onCanStartChange: (canStart: boolean) => void }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const norms = useSelector(getNorms);
  const version = useAppSelector(selectActiveVersion);
  const projectId = useAppSelector((s) => s.workspace.project?.id);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const details = useAppSelector((s) => s.workspace.lampSimilarityDetails);
  const isLoadingDetails = useAppSelector((s) => s.workspace.isLoadingLampSimilarityDetails);
  const applied = useAppSelector((s) => s.workspace.appliedLampSimilaritySelection);
  const isCreatingVersion = useAppSelector((s) => s.workspace.isCreatingVersion);
  const userFeatureFlags = useAppSelector(getUserFeatureFlags);
  const toVersionEnabled = userFeatureFlags.includes(MARKET_MATCH_TO_VERSION_FLAG);
  const measurementData = useAppSelector(selectWorkspaceMeasurementData);
  const [preview, setPreview] = useState<{
    lampName: string;
    aggregateResults: NonNullable<RankedCandidate['aggregateResults']>;
  } | null>(null);

  const running = isJobInProgress(version?.lampSimilarityJob);
  const failed = isJobFailed(version?.lampSimilarityJob);
  const candidates = useMemo(() => version?.lampSimilarityResult?.candidates ?? [], [version]);

  // Details (names, images, apply rules) load lazily, and are cleared on a version switch.
  useEffect(() => {
    if (candidates.length > 0 && details.length === 0 && !isLoadingDetails) dispatch(loadLampSimilarityDetails());
  }, [candidates.length, details.length, isLoadingDetails, dispatch]);

  const layerDisplayNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const layer of measurementData ?? []) {
      names[layer.name] = modelLayers.find((l) => l.threeJsName === layer.name)?.originalName || layer.name;
    }
    return names;
  }, [measurementData, modelLayers]);

  const header = (
    <PanelSection
      title={t('workspace.marketMatches')}
      count={candidates.length > 0 ? candidates.length : undefined}
      actions={
        failed ? (
          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-red-500/20 text-red-400">
            {t('common.failed')}
          </span>
        ) : undefined
      }
    >
      {running ? (
        <MarketSearchProgress startedAt={version?.lampSimilarityJob?.createdAt} />
      ) : candidates.length === 0 ? (
        <MarketMatchPrefiltersPanel
          formId={MARKET_PREFILTERS_FORM_ID}
          onCanStartChange={onCanStartChange}
          onStart={(filters) => {
            if (projectId != null && version) {
              dispatch(startLampSimilarity({ projectId, versionId: version.id, filters }));
            }
          }}
        />
      ) : isLoadingDetails ? (
        <div className="flex items-center gap-2 px-4 py-5 text-[13px] text-gray-400">
          <Loader2 size={16} className="animate-spin" />
          {t('workspace.loadingLampDetails')}
        </div>
      ) : (
        <div className="p-2 space-y-1">
          <div className="flex items-center gap-2.5 px-2 pt-1 pb-2.5 mb-1 border-b border-white/5">
            <CheckCircle2 size={18} className="shrink-0 text-lime-400" />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-white">{t('workspace.matchingCompleted')}</p>
              <p className="text-[11px] text-gray-400">
                {t('workspace.panel.market.found', { count: candidates.length })}
              </p>
            </div>
          </div>
          {candidates.map((candidate) => {
            const fileId = candidate.measurementFile?.id;
            const detail = details.find((d) => d.lampId === candidate.lampId && d.measurementFileId === fileId);
            const previewMode = getMarketMatchPreviewMode(userFeatureFlags, detail, version);
            const canApply = canApplyMarketMatchCandidate(userFeatureFlags, detail, version);
            const isApplied = applied?.lampId === candidate.lampId && applied?.measurementFileId === fileId;
            const aggregate =
              isApplied && measurementData?.length
                ? measurementDataToAggregateResults(measurementData)
                : candidate.aggregateResults;
            const targets = aggregate?.length
              ? getTargetsSatisfiedCountFromAggregateResults(aggregate)
              : getTargetsSatisfiedCount(candidate);
            const allMet = targets.total > 0 ? targets.satisfied === targets.total : candidate.satisfiesNorms;
            const name = detail?.name || t('workspace.lampId', { id: candidate.lampId });

            return (
              <div
                key={`${candidate.lampId}-${fileId ?? 'no-file'}`}
                className="group flex gap-3 p-2 rounded-lg border border-transparent hover:border-white/10 hover:bg-white/[0.04] transition-colors"
              >
                {detail?.imageUrl && (
                  <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center p-1">
                    <img src={detail.imageUrl} alt={name} className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {detail?.manufacturerName && (
                    <p className="text-[11px] text-gray-400 truncate">{detail.manufacturerName}</p>
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[13px] font-semibold text-white truncate" title={name}>
                      {name}
                    </h3>
                    <span className="shrink-0 text-[12px] font-semibold text-lime-400 tabular-nums">
                      {candidate.lumenOutput > 0 ? Math.round(candidate.lumenOutput).toLocaleString() : 'N/A'}{' '}
                      <span className="font-normal opacity-70">lm</span>
                    </span>
                  </div>
                  <a
                    href={buildCatalogueUrl({
                      productId: candidate.lampId,
                      seriesId: detail?.seriesId,
                      manufacturerId: detail?.manufacturerId,
                    })}
                    className="text-[11px] text-lime-400 hover:underline"
                  >
                    {t('workspace.viewInCatalogue')}
                  </a>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      disabled={!aggregate?.length}
                      onClick={() => aggregate?.length && setPreview({ lampName: name, aggregateResults: aggregate })}
                      className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold transition-colors ${
                        allMet
                          ? 'text-lime-400 bg-lime-500/10 hover:bg-lime-500/20'
                          : 'text-red-400 bg-red-500/10 hover:bg-red-500/20'
                      } ${aggregate?.length ? '' : 'cursor-default'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${allMet ? 'bg-lime-500' : 'bg-red-500'}`} />
                      {t('workspace.targetsSatisfied', { satisfied: targets.satisfied, total: targets.total })}
                    </button>
                    <div className="flex items-center gap-1.5">
                      {canApply && previewMode && (
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(
                              workspaceReducer.actions.applyLampSimilarityMeasurement({
                                lampId: candidate.lampId,
                                measurementFileId: fileId,
                                previewMode,
                              }),
                            )
                          }
                          className={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                            isApplied
                              ? 'bg-lime-500 text-black'
                              : 'bg-white/10 text-white hover:bg-lime-500 hover:text-black'
                          }`}
                        >
                          {isApplied ? t('workspace.applied') : t('workspace.apply')}
                        </button>
                      )}
                      {toVersionEnabled && (
                        <button
                          type="button"
                          disabled={isCreatingVersion}
                          onClick={() => {
                            if (projectId == null || !version || isCreatingVersion) return;
                            dispatch(
                              createVersionFromMarketMatch({
                                projectId,
                                sourceVersionId: version.id,
                                lampId: candidate.lampId,
                                measurementFileId: fileId,
                              }),
                            );
                          }}
                          className="rounded px-2 py-0.5 text-[11px] font-semibold bg-white/10 text-white hover:bg-lime-500 hover:text-black transition-colors disabled:opacity-50 disabled:cursor-wait"
                        >
                          {t('workspace.toNewVersion')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PanelSection>
  );

  return (
    <>
      {header}
      <MarketMatchResultsPreviewModal
        isOpen={!!preview}
        lampName={preview?.lampName || ''}
        aggregateResults={preview?.aggregateResults || []}
        layerDisplayNames={layerDisplayNames}
        norms={norms}
        onClose={() => setPreview(null)}
      />
    </>
  );
}

/** Running market search: a centred spinner with what is happening and for how long. */
function MarketSearchProgress({ startedAt }: { startedAt?: Date | string }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const started = startedAt ? new Date(startedAt).getTime() : NaN;
  const seconds = Number.isFinite(started) ? Math.max(0, Math.floor((now - started) / 1000)) : null;
  const elapsed = seconds === null ? null : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-8 text-center">
      <div className="relative h-32 w-32">
        <svg viewBox="0 0 120 120" className="h-full w-full animate-[spin_1.8s_linear_infinite]" aria-hidden>
          <defs>
            <linearGradient id="market-search-arc" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#B3EC49" stopOpacity="0" />
              <stop offset="100%" stopColor="#B3EC49" />
            </linearGradient>
          </defs>
          <circle cx="60" cy="60" r="52" stroke="rgba(255,255,255,0.07)" strokeWidth="8" fill="none" />
          <circle
            cx="60"
            cy="60"
            r="52"
            stroke="url(#market-search-arc)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray="200 127"
            fill="none"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-lime-400">
          <MarketMatchIcon size={36} />
        </div>
      </div>
      <h3 className="mt-6 text-[15px] font-semibold text-white">{t('workspace.panel.market.searchingTitle')}</h3>
      <p className="mt-2 max-w-[260px] text-[13px] leading-relaxed text-gray-400">
        {t('workspace.panel.market.searchingText')}
      </p>
      {elapsed && (
        <p className="mt-4 text-[12px] tabular-nums text-gray-500">
          {t('workspace.panel.market.runningFor', { time: elapsed })}
        </p>
      )}
    </div>
  );
}
